import React, { Suspense, lazy, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { Toaster } from '@/components/ui/sonner';
import { useMediaQuery } from '@/hooks/use-media-query';
import { Skeleton } from '@/components/ui/skeleton';
import Navbar from './components/Navbar';
import CartDrawer from './components/CartDrawer';
import GuestUpgradeBanner from './components/GuestUpgradeBanner';
import LandingPage from './pages/LandingPage';
import CatalogPage from './pages/CatalogPage';
import LoginPage from './pages/LoginPage';
import AnnouncementsHost from './components/AnnouncementsHost';
import AdminSidebar from './components/AdminSidebar';
import AdminMobileTopBar from './components/AdminMobileTopBar';
import AdminStoreViewBanner from './components/AdminStoreViewBanner';
import AppErrorBoundary from './components/AppErrorBoundary';
import {
  AdminStoreViewProvider,
  useAdminStoreView,
} from './context/AdminStoreViewContext';

// Secondary customer pages are code-split too: the first paint only ships the
// landing, login, and catalog routes. LandingPage, LoginPage, and CatalogPage
// stay eager because they are first-paint routes.
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const OrderConfirmationPage = lazy(() => import('./pages/OrderConfirmationPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));

// Admin pages are code-split: customers never download the admin bundle.
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminSalesReport = lazy(() => import('./pages/admin/AdminSalesReport'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminFulfillment = lazy(() => import('./pages/admin/AdminFulfillment'));
const AdminCustomers = lazy(() => import('./pages/admin/AdminCustomers'));
const AdminInviteCodes = lazy(() => import('./pages/admin/AdminInviteCodes'));
const AdminTickets = lazy(() => import('./pages/admin/AdminTickets'));
const AdminBenefitCodes = lazy(() => import('./pages/admin/AdminBenefitCodes'));
const AdminAnnouncements = lazy(() => import('./pages/admin/AdminAnnouncements'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminEmailTemplates = lazy(() => import('./pages/admin/AdminEmailTemplates'));

const IS_READ_ONLY_STAGING = process.env.REACT_APP_READ_ONLY_MODE === 'true';
const DEPLOYMENT_ENVIRONMENT = (process.env.REACT_APP_ENVIRONMENT || '').toLowerCase();

function StagingSafetyBanner() {
  if (!IS_READ_ONLY_STAGING) return null;
  const message = DEPLOYMENT_ENVIRONMENT === 'staging'
    ? 'Firebase staging preview. Read-only mode is active. Orders, account changes, emails, and admin updates are blocked.'
    : 'Temporary maintenance mode is active. Orders and account changes are currently blocked.';
  return (
    <div
      role="status"
      className="sticky top-0 z-[100] border-b border-[hsl(var(--notice-foreground)/0.25)] bg-[hsl(var(--notice))] px-4 py-2 text-center text-sm font-semibold text-[hsl(var(--notice-foreground))] shadow-sm"
    >
      {message}
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  if (!user) return <Navigate to="/login" replace state={{ returnTo: location.pathname + location.search + location.hash }} />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const {
    isStoreView,
    exitStoreView,
    isStoreViewEntryPending,
  } = useAdminStoreView();

  useEffect(() => {
    if (isStoreView && !isStoreViewEntryPending()) exitStoreView();
  }, [exitStoreView, isStoreView, isStoreViewEntryPending]);

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  if (!user) return <Navigate to="/login" replace state={{ returnTo: location.pathname + location.search + location.hash }} />;
  if (user.role !== 'admin') return <Navigate to="/catalog" replace />;
  return (
    <div className="admin-portal flex min-h-screen">
      <AdminSidebar />
      <div className="flex-1 md:ml-64 flex flex-col min-w-0">
        <AdminMobileTopBar />
        <div className="flex-1 min-w-0">
          <Suspense fallback={<div className="p-6 space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-64 rounded-2xl" /></div>}>
            {children}
          </Suspense>
        </div>
      </div>
    </div>
  );
}

function StorefrontRoute({ children, showGuestUpgrade = true }) {
  const { isStoreView, completeStoreViewEntry } = useAdminStoreView();

  useEffect(() => {
    if (isStoreView) completeStoreViewEntry();
  }, [completeStoreViewEntry, isStoreView]);

  return (
    <ProtectedRoute>
      <Navbar />
      <AdminStoreViewBanner className="top-16" />
      {showGuestUpgrade && <GuestUpgradeBanner />}
      {children}
    </ProtectedRoute>
  );
}

function AppRoutes() {
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const routes = (
    <Suspense fallback={<div className="p-6 space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-64 rounded-2xl" /></div>}>
    <Routes location={location}>
      <Route path="/" element={<LandingPage />} />
      <Route path="/catalog" element={<StorefrontRoute><CatalogPage /></StorefrontRoute>} />
      <Route path="/products/:id" element={<StorefrontRoute><ProductDetailPage /></StorefrontRoute>} />
      <Route path="/cart" element={<StorefrontRoute><CartPage /></StorefrontRoute>} />
      <Route path="/checkout" element={<StorefrontRoute><CheckoutPage /></StorefrontRoute>} />
      <Route path="/order-confirmation/:orderId" element={<StorefrontRoute showGuestUpgrade={false}><OrderConfirmationPage /></StorefrontRoute>} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/account/*" element={<StorefrontRoute showGuestUpgrade={false}><AccountPage /></StorefrontRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/sales" element={<AdminRoute><AdminSalesReport /></AdminRoute>} />
      <Route path="/admin/analytics" element={<AdminRoute><AdminAnalytics /></AdminRoute>} />
      <Route path="/admin/products" element={<AdminRoute><AdminProducts /></AdminRoute>} />
      <Route path="/admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
      <Route path="/admin/fulfillment" element={<AdminRoute><AdminFulfillment /></AdminRoute>} />
      <Route path="/admin/orders/:orderId" element={<AdminRoute><AdminOrders /></AdminRoute>} />
      <Route path="/admin/customers" element={<AdminRoute><AdminCustomers /></AdminRoute>} />
      <Route path="/admin/invite-codes" element={<AdminRoute><AdminInviteCodes /></AdminRoute>} />
      <Route path="/admin/tickets" element={<AdminRoute><AdminTickets /></AdminRoute>} />
      <Route path="/admin/benefit-codes" element={<AdminRoute><AdminBenefitCodes /></AdminRoute>} />
      <Route path="/admin/announcements" element={<AdminRoute><AdminAnnouncements /></AdminRoute>} />
      <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
      <Route path="/admin/email-templates" element={<AdminRoute><AdminEmailTemplates /></AdminRoute>} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    </Suspense>
  );
  if (reduceMotion) return routes;
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
      >
        {routes}
      </motion.div>
    </AnimatePresence>
  );
}

function ResponsiveToaster() {
  // Mobile (<md): center top — avoids overlap with the hamburger button at the
  // top-left and the page CTA at the top-right of AdminMobileTopBar.
  // Desktop (≥md): preserve the existing top-right anchor.
  const isDesktop = useMediaQuery('(min-width: 768px)');
  return <Toaster position={isDesktop ? 'top-right' : 'top-center'} richColors />;
}

function App() {
  return (
    <AppErrorBoundary>
    <AuthProvider>
      <AdminStoreViewProvider>
        <CartProvider>
          <BrowserRouter>
            <div className="min-h-screen bg-background">
              <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[110] focus:border focus:border-border focus:bg-card focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
                data-testid="skip-to-content"
              >
                Skip to content
              </a>
              <StagingSafetyBanner />
              <AnnouncementsHost />
              <AppRoutes />
              <CartDrawer />
              <ResponsiveToaster />
            </div>
          </BrowserRouter>
        </CartProvider>
      </AdminStoreViewProvider>
    </AuthProvider>
    </AppErrorBoundary>
  );
}

export default App;
